import { PageHeading } from '@/components/base/page-heading';
import { cn } from '@/utils';
import { type StepsType as StepsTypeAlias } from '@graphql/index';
import {
  Clock,
  ExternalLink,
  Shield,
  Target,
  TrendingUp,
  Zap,
} from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { steps, type StepsType } from '../configuration';
import {
  EnterpriseRadioGroup,
  EnterpriseRadioGroupItem,
} from './enterprise-group';

interface SelectAccountsizeProps {
  initialState?: StepsType;
  onChange: (value: StepsType | StepsTypeAlias) => Promise<void>;
}

// Enhanced badge component for premium feel
function PremiumBadge({
  children,
  variant = 'default',
  className = '',
  icon: Icon,
}: {
  children: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'info' | 'premium';
  className?: string;
  icon?: React.ComponentType<{ className?: string }>;
}): JSX.Element {
  const variants = {
    default:
      'bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-zinc-700',
    success:
      'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200/50 dark:border-emerald-800/50',
    warning:
      'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200/50 dark:border-amber-800/50',
    info: 'bg-blue-100 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200/50 dark:border-blue-800/50',
    premium:
      'bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-950/40 dark:to-pink-950/40 text-purple-700 dark:text-purple-300 border-purple-200/50 dark:border-purple-800/50',
  };

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium shadow-sm transition-all duration-200',
        variants[variant],
        className,
      )}
    >
      {Icon && <Icon className="h-3 w-3" />}
      {children}
    </div>
  );
}

// Get appropriate icon for each step type
const getStepIcon = (
  stepType: StepsType,
): React.ComponentType<{ className?: string }> => {
  const stepTypeStr = String(stepType);
  switch (stepTypeStr) {
    case 'InstantFundedChallenge':
      return Zap;
    case 'OneStepChallenge':
      return Target;
    case 'TwoStepsChallenge':
      return TrendingUp;
    case 'ThreeStepsChallenge':
      return Shield;
    default:
      return Target;
  }
};

// Get appropriate variant for each step
const getStepVariant = (
  stepType: StepsType,
): 'default' | 'success' | 'warning' | 'info' | 'premium' => {
  const stepTypeStr = String(stepType);
  switch (stepTypeStr) {
    case 'InstantFundedChallenge':
      return 'premium';
    case 'OneStepChallenge':
      return 'success';
    case 'TwoStepsChallenge':
      return 'info';
    case 'ThreeStepsChallenge':
      return 'warning';
    default:
      return 'default';
  }
};

// Get icon background classes based on variant
const getIconBackgroundClasses = (variant: string): string => {
  switch (variant) {
    case 'premium':
      return 'bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-950/40 dark:to-pink-950/40 text-purple-600 dark:text-purple-400';
    case 'success':
      return 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400';
    case 'info':
      return 'bg-blue-100 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400';
    case 'warning':
      return 'bg-amber-100 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400';
    default:
      return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400';
  }
};

export function SelectAccountSteps({
  initialState,
  onChange = () => Promise.resolve(),
}: SelectAccountsizeProps): JSX.Element {
  const key = initialState;
  const { t } = useTranslation();

  async function handleOnChange(value: StepsType): Promise<void> {
    await onChange(value);
  }

  return (
    <div key={key} id="select-account-steps-section" className="space-y-8">
      {/* Enhanced Page Heading */}
      <div className="space-y-4 text-center">
        <PageHeading
          size="xxlarge"
          subheading={t('selectAccountSteps.subheading')}
          className="text-gray-900 dark:text-white"
        >
          {t('selectAccountSteps.heading')}
        </PageHeading>

        {/* This subtitle can be removed or kept simple since we have the main subheading */}
      </div>

      {/* Enhanced Radio Group */}
      <EnterpriseRadioGroup
        onValueChange={(v: StepsType) => {
          onChange(v);
        }}
        defaultValue={initialState}
        className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4"
      >
        {steps.map((item) => {
          const StepIcon = getStepIcon(item.value);
          const stepVariant = getStepVariant(item.value);
          const iconBackgroundClasses = getIconBackgroundClasses(stepVariant);

          return (
            <EnterpriseRadioGroupItem
              onClick={async () => {
                await handleOnChange(item.value);
              }}
              key={item.title}
              value={item.value}
              data-testid={String(item.title)}
              decoration={
                item.decoration && (
                  <PremiumBadge
                    variant="premium"
                    className="rotate-12 font-bold shadow-lg"
                  >
                    {item.decoration}
                  </PremiumBadge>
                )
              }
            >
              {/* Label Badge */}
              {item.label && (
                <div className="absolute left-4 top-4">
                  <PremiumBadge variant={stepVariant} icon={StepIcon}>
                    {item.label}
                  </PremiumBadge>
                </div>
              )}

              <div className="flex h-full flex-col space-y-4">
                {/* Header */}
                <div
                  className={cn(
                    'flex items-center gap-3 transition-colors duration-200',
                    item.label && 'mt-6',
                  )}
                >
                  <div
                    className={cn(
                      'rounded-lg p-2 transition-all duration-200',
                      iconBackgroundClasses,
                    )}
                  >
                    <StepIcon className="h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {item.title}
                  </h3>
                </div>

                {/* Description List */}
                <div className="flex-1 space-y-3">
                  {item.description.map((desc) => (
                    <div key={desc} className="flex items-start gap-3">
                      <div className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400 dark:bg-gray-500" />
                      <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                        {t(`selectAccountSteps.${desc}`)}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Rules Link */}
                <div className="border-t border-gray-200 pt-4 dark:border-zinc-700">
                  <a
                    className="group inline-flex items-center gap-2 text-sm font-medium text-blue-600 transition-colors duration-200 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    href={item.rules}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <span>{t('selectAccountSteps.viewRules')}</span>
                    <ExternalLink className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </a>
                </div>
              </div>
            </EnterpriseRadioGroupItem>
          );
        })}
      </EnterpriseRadioGroup>

      {/* Additional Information */}
      <div className="space-y-4 text-center">
        <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Clock className="h-4 w-4" />
          <span>{t('selectAccountSteps.processingTime')}</span>
        </div>

        <div className="flex items-center justify-center gap-6 text-xs text-gray-400 dark:text-gray-500">
          <div className="flex items-center gap-1">
            <Shield className="h-3 w-3" />
            <span>{t('selectAccountSteps.secure')}</span>
          </div>
          <div className="flex items-center gap-1">
            <Target className="h-3 w-3" />
            <span>{t('selectAccountSteps.professional')}</span>
          </div>
          <div className="flex items-center gap-1">
            <TrendingUp className="h-3 w-3" />
            <span>{t('selectAccountSteps.optimized')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
